/**
 * AssemblyGraph 装配图 —— 「实验装置是否搭好」不看图片坐标，看图关系。
 * 对应技术设计 §5.1（查询形态）与 §11.2（数据结构）。
 *
 * 全部操作是纯函数：输入图返回新图，不就地修改。
 */

import type { LabObject } from "./lab-object.js";
import type { Endpoint, Relation, RelationType } from "./relation.js";
import { endpointMatches } from "./relation.js";

/**
 * 端点模式：TargetGraph 用它描述「哪一类端点」。
 * 技术设计 §11.2 只写了 `from: EndpointPattern` 而未给出字段，此处补齐：
 * 允许按具体对象 id 或按器材 type 匹配，端口/槽位可选。消费方是 P2 的 Mission 目标校验。
 */
export interface EndpointPattern {
  objectId?: string;
  objectType?: string;
  portId?: string;
  slotId?: string;
}

export interface RelationRequirement {
  id: string;
  type: RelationType;
  from: EndpointPattern;
  to: EndpointPattern;
  stepId?: string;
  requiredFor?: "setup" | "reaction" | "collection" | "safety";
}

export interface TargetGraph {
  id: string;
  requiredRelations: RelationRequirement[];
  optionalRelations?: RelationRequirement[];
}

export interface AssemblyGraph {
  nodes: Readonly<Record<string, LabObject>>;
  relations: Readonly<Record<string, Relation>>;
  target?: TargetGraph;
}

export function createAssemblyGraph(objects: LabObject[], target?: TargetGraph): AssemblyGraph {
  const nodes: Record<string, LabObject> = {};
  for (const object of objects) {
    nodes[object.id] = object;
  }
  return target ? { nodes, relations: {}, target } : { nodes, relations: {} };
}

export function addRelation(graph: AssemblyGraph, relation: Relation): AssemblyGraph {
  return {
    ...graph,
    relations: { ...graph.relations, [relation.id]: relation },
  };
}

export function removeRelation(graph: AssemblyGraph, relationId: string): AssemblyGraph {
  if (!(relationId in graph.relations)) return graph;
  const relations = { ...graph.relations };
  delete relations[relationId];
  return { ...graph, relations };
}

export function updateObject(
  graph: AssemblyGraph,
  objectId: string,
  patch: Partial<LabObject>,
): AssemblyGraph {
  const current = graph.nodes[objectId];
  if (!current) return graph;
  return {
    ...graph,
    nodes: { ...graph.nodes, [objectId]: { ...current, ...patch } },
  };
}

export function listObjects(graph: AssemblyGraph): LabObject[] {
  return Object.values(graph.nodes);
}

export function listRelations(graph: AssemblyGraph): Relation[] {
  return Object.values(graph.relations);
}

/**
 * §5.1 的核心查询。有向：from → to。
 * 端点只给 objectId 时匹配该对象上的任意端口/槽位，例如
 * `hasRelation(graph, { objectId: "test_tube_1", portId: "mouth" }, { objectId: "cork_1" }, "insert")`。
 * 只统计 active 关系，preview / broken 不算装好。
 */
export function hasRelation(
  graph: AssemblyGraph,
  from: Endpoint,
  to: Endpoint,
  type?: RelationType,
): boolean {
  return listRelations(graph).some(
    (relation) =>
      relation.state === "active" &&
      (type === undefined || relation.type === type) &&
      endpointMatches(relation.from, from) &&
      endpointMatches(relation.to, to),
  );
}

/** 某个器材参与的全部关系（不分方向），P1 的约束求解与 P2 的规则判定都要用。 */
export function relationsOf(graph: AssemblyGraph, objectId: string): Relation[] {
  return listRelations(graph).filter(
    (relation) => relation.from.objectId === objectId || relation.to.objectId === objectId,
  );
}
